import { getCsrfToken } from "@/integrations/php/client";

export type OperationsStatus = "active" | "open" | "acknowledged";
export type OperationsSeverity = "all" | "critical" | "warning" | "info";
export type OperationsCategory = "all" | "unassigned" | "sla" | "notifications" | "meta";

export interface OperationsAlert {
  id: number;
  alert_type: string;
  severity: "critical" | "warning" | "info";
  status: "open" | "acknowledged";
  title: string;
  detail_code?: string | null;
  lead_id?: string | null;
  notification_job_id?: number | null;
  meta_event_id?: number | null;
  lead_name?: string | null;
  lead_service?: string | null;
  lead_address?: string | null;
  lead_created_at?: string | null;
  acknowledged_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperationsSummary {
  active: number;
  open: number;
  acknowledged: number;
  critical: number;
  oldest_open_at: string | null;
  types: Record<string, number>;
}

export interface OperationsFilters {
  page: number;
  perPage: number;
  status: OperationsStatus;
  severity: OperationsSeverity;
  category: OperationsCategory;
  search: string;
}

export interface OperationsResponse {
  summary: OperationsSummary;
  alerts: OperationsAlert[];
  pagination: { page: number; per_page: number; total: number; pages: number };
  filters: { status: OperationsStatus; severity: OperationsSeverity; category: OperationsCategory; q: string };
}

export interface PartnerSuggestion {
  id: string;
  name: string;
  location: string;
  distance_km: number | null;
  radius_km: number;
  within_radius: boolean;
}

async function requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const csrfToken = await getCsrfToken();
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      ...init.headers,
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error?.message || "Η ενέργεια δεν ολοκληρώθηκε.");
  }
  return body.data as T;
}

export async function fetchOperations(filters: OperationsFilters, refresh = false): Promise<OperationsResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    per_page: String(filters.perPage),
    status: filters.status,
    severity: filters.severity,
    category: filters.category,
  });
  if (filters.search.trim()) params.set("q", filters.search.trim());
  if (refresh) params.set("refresh", "1");
  return requestJson<OperationsResponse>(`/api/admin/operations.php?${params.toString()}`);
}

export async function updateOperationalAlert(
  alertId: number,
  action: "acknowledge" | "resolve",
): Promise<{ updated: boolean }> {
  return requestJson<{ updated: boolean }>("/api/admin/operations.php", {
    method: "POST",
    body: JSON.stringify({ alert_id: alertId, action }),
  });
}

export async function fetchPartnerSuggestions(leadId: string): Promise<PartnerSuggestion[]> {
  const data = await requestJson<{ suggestions: PartnerSuggestion[] }>(
    `/api/admin/operations.php?lead_id=${encodeURIComponent(leadId)}`,
  );
  return data.suggestions || [];
}

export async function assignLeadToPartner(leadId: string, partnerId: string): Promise<void> {
  await requestJson("/api/admin/assign-lead.php", {
    method: "POST",
    body: JSON.stringify({ lead_id: leadId, partner_id: partnerId }),
  });
}
